import {useCallback, useState} from 'react';

export interface FileTab {
  kind: 'file';
  id: string;
  chatSessionId: string;
  externalFileId: string;
  filename: string;
  mimeType: string;
  label: string;
}

export interface ArtifactTab {
  kind: 'artifact';
  id: string;
  label: string;
  Component: React.ComponentType<{data: unknown; editable: boolean}>;
  data: unknown;
  editable: boolean;
}

export type ViewerTab = FileTab | ArtifactTab;

export interface OpenTabOptions {
  position?: 'start' | 'end';
}

export interface UseTabbedViewerResult {
  tabs: Array<ViewerTab>;
  activeTab: ViewerTab | null;
  openTab: (tab: ViewerTab, options?: OpenTabOptions) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
}

interface TabState {
  tabs: Array<ViewerTab>;
  activeTabId: string | null;
}

export function useTabbedViewer(): UseTabbedViewerResult {
  const [state, setState] = useState<TabState>({tabs: [], activeTabId: null});

  const openTab = useCallback((tab: ViewerTab, options?: OpenTabOptions) => {
    const position = options?.position ?? 'end';
    setState((prev) => {
      if (prev.tabs.some((t) => t.id === tab.id)) {
        const tabs = prev.tabs.map((t) => (t.id === tab.id ? tab : t));
        return {tabs, activeTabId: tab.id};
      }
      const tabs = position === 'start' ? [tab, ...prev.tabs] : [...prev.tabs, tab];
      return {tabs, activeTabId: tab.id};
    });
  }, []);

  const closeTab = useCallback((tabId: string) => {
    setState((prev) => {
      const index = prev.tabs.findIndex((t) => t.id === tabId);
      if (index === -1) {
        return prev;
      }
      const updated = prev.tabs.filter((t) => t.id !== tabId);
      let nextActiveTabId = prev.activeTabId;
      if (prev.activeTabId === tabId) {
        if (updated.length === 0) {
          nextActiveTabId = null;
        } else {
          nextActiveTabId = updated[Math.min(index, updated.length - 1)].id;
        }
      }
      return {tabs: updated, activeTabId: nextActiveTabId};
    });
  }, []);

  const setActiveTab = useCallback((tabId: string) => {
    setState((prev) => ({...prev, activeTabId: tabId}));
  }, []);

  const activeTab = state.tabs.find((t) => t.id === state.activeTabId) ?? null;

  return {
    tabs: state.tabs,
    activeTab,
    openTab,
    closeTab,
    setActiveTab,
  };
}
